import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useRecoilValue } from "recoil";

import styles from "./SpaceAddModal.module.css";

import { inProgressSpaceListState } from "../../../recoil/spaces/inProgressSpaceListState";
import {
  createSpacePayload,
  getNextAvailableSpaceColor,
} from "../../../services/space/space.service";
import { useCreateSpaceMutation } from "../../../react-query/space/core/useCreateSpaceMutation";
import { useJuneTrackCall } from "../../../utils/june/analytics";

interface SpaceAddModalProps {
  onClose: () => void;
}
export default function SpaceAddModal({ onClose }: SpaceAddModalProps) {
  const spaceList = useRecoilValue(inProgressSpaceListState);
  const mainRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const backgroundRef = useRef<HTMLDivElement | null>(null);

  const { mutate: createSpace } = useCreateSpaceMutation();

  const trackCall = useJuneTrackCall();

  const handleAddProject = (newProjectTitle: string) => {
    const newItem = createSpacePayload(newProjectTitle, spaceList);

    createSpace(newItem, {
      onSuccess: () => {
        trackCall("create_space", { location: "task_page" });
      },
    });
  };

  const handleCreate = () => {
    if (!inputRef.current || !inputRef.current.value) {
      onClose();
      return;
    }

    handleAddProject(inputRef.current.value);
    onClose();
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key === "Enter") {
      e.preventDefault();
      handleCreate();
    }
  };

  const handleBackgroundClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
  };

  // NOTE: 마우스 클릭으로 모달 열면 포커스가 안됨
  useEffect(() => {
    // setTimeout을 사용하여 DOM 업데이트 이후에 focus 호출하도록 임시 수정
    const inputFocusDelayTimer = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);

    return () => clearTimeout(inputFocusDelayTimer);
  }, []);

  return createPortal(
    <div
      className={styles["wrap"]}
      ref={backgroundRef}
      onMouseDown={handleBackgroundClick}
    >
      <div
        className={styles["main"]}
        ref={mainRef}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles["header"]}>
          <span className={styles["header-title"]}>Add a space</span>
          <span
            className={styles["header-close"]}
            onClick={onClose}
          >
            X
          </span>
        </div>
        <div className={styles["body"]}>
          <div
            className={styles["body-icon"]}
            style={{
              backgroundColor: `${getNextAvailableSpaceColor(spaceList)}`,
            }}
          ></div>
          <input
            ref={inputRef}
            tabIndex={0}
            autoFocus
            className={styles["body-input"]}
            onKeyDown={handleInputKeyDown}
          />
        </div>
        <div
          className={styles["footer"]}
          onClick={handleCreate}
        >
          Create
        </div>
      </div>
    </div>,
    document.body
  );
}
